import React, {useState, useEffect, useRef} from 'react';
import {Message, toaster} from "rsuite";

const YandexMapComponent = ({onUseCoordinates, inputCoordinates}) => {
  const [coordinates, setCoordinates] = useState(null);
  const [address, setAddress] = useState('');
  const [weather, setWeather] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const placemarkRef = useRef(null);
  const searchInputRef = useRef(null);
  const updateTimeoutRef = useRef(null);
  const lastWeatherCoordinatesRef = useRef(null);
  const weatherRequestIdRef = useRef(0);
  const apiKey = 'b030dfb7-b5b0-433a-b396-eb046802320b';

  const getWeatherDataJSONP = (lat, lng) => {
    return new Promise((resolve) => {
      const apiKey = 'f247f977fa06cb766ba7c3dc1013ad50';
      const callbackName = 'weatherCallback_' + Date.now();
      const script = document.createElement('script');
      let finished = false;
      let timeoutId = null;

      const cleanup = () => {
        if (finished) {
          return;
        }

        finished = true;

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }

        delete window[callbackName];
      };

      script.src = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&appid=${apiKey}&units=metric&lang=ru&callback=${callbackName}`;

      window[callbackName] = (data) => {
        cleanup();

        if (data.cod === 200) {
          resolve({
            temperature: Math.round(data.main.temp),
            pressure: Math.round(data.main.pressure * 0.75),
            humidity: data.main.humidity,
            description: data.weather[0].description,
            icon: data.weather[0].icon,
            feelsLike: Math.round(data.main.feels_like),
            windSpeed: data.wind.speed
          });
        } else {
          resolve(null);
        }
      };

      script.onerror = () => {
        cleanup();
        resolve(null);
      };

      document.head.appendChild(script);

      timeoutId = setTimeout(() => {
        if (window[callbackName]) {
          cleanup();
          resolve(null);
        }
      }, 10000);
    });
  };

  const fetchWeather = async (lat, lng, options = {}) => {
    const { force = false } = options;
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;

    if (!force && lastWeatherCoordinatesRef.current === key) {
      return;
    }

    lastWeatherCoordinatesRef.current = key;
    weatherRequestIdRef.current += 1;
    const requestId = weatherRequestIdRef.current;

    setLoadingWeather(true);

    try {
      const weatherData = await getWeatherDataJSONP(lat, lng);

      if (weatherRequestIdRef.current === requestId) {
        setWeather(weatherData);
      }

      return weatherData;
    } finally {
      if (weatherRequestIdRef.current === requestId) {
        setLoadingWeather(false);
      }
    }
  };

  const parseCoordinate = (direction, value) => {
    if (!value || typeof value !== 'string') {
      return null;
    }

    try {
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts.length !== 2) {
          return null;
        }

        const degrees = parseInt(parts[0], 10);
        const fraction = parseInt(parts[1], 10) / 10000;

        if (isNaN(degrees) || isNaN(fraction)) {
          return null;
        }

        let result = degrees + fraction;

        if (direction === 'S' || direction === 'W') {
          result = -result;
        }

        return result;
      } else {
        const cleanValue = value.replace(/[^\d]/g, '');
        if (cleanValue.length !== 7) {
          return null;
        }

        const degrees = parseInt(cleanValue.substring(0, 3), 10);
        const fraction = parseInt(cleanValue.substring(3), 10) / 10000;

        if (isNaN(degrees) || isNaN(fraction)) {
          return null;
        }

        let result = degrees + fraction;

        if (direction === 'S' || direction === 'W') {
          result = -result;
        }

        return result;
      }
    } catch (error) {
      console.error('Ошибка парсинга координат:', error);
      return null;
    }
  };

  const isCoordinateComplete = (coordinate) => {
    if (!coordinate) {
      return false;
    }

    if (coordinate.includes('.')) {
      const parts = coordinate.split('.');
      return parts.length === 2 &&
        parts[0].replace(/[^\d]/g, '').length === 3 &&
        parts[1].replace(/[^\d]/g, '').length === 4;
    } else {
      const cleanValue = coordinate.replace(/[^\d]/g, '');
      return cleanValue.length === 7;
    }
  };

  const createCustomPlacemark = (coords, content = '') => {
    return new window.ymaps.Placemark(coords, {
      balloonContent: content || `Выбранная точка: ${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`
    }, {
      iconLayout: 'default#image',
      iconImageHref: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="52" height="62" viewBox="0 0 52 62" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M26 1C17.7157 1 11 7.71573 11 16C11 25 26 41 26 41C26 41 41 25 41 16C41 7.71573 34.2843 1 26 1Z" fill="#2196F3" stroke="white" stroke-width="3"/>
        <circle cx="26" cy="16" r="4" fill="white"/>
      </svg>
    `),
      iconImageSize: [52, 62],
      iconImageOffset: [-26, -62]
    });
  };

  useEffect(() => {
    if (window.ymaps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=ru_RU&load=package.full`;
    script.async = true;

    script.onload = () => {
      window.ymaps.ready(() => {
        setMapLoaded(true);
      });
    };

    script.onerror = (error) => {
      console.error('Failed to load Yandex Maps API:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка загрузки карты
        </Message>,
        {placement: 'topCenter', duration: 3000}
      );
    };

    document.head.appendChild(script);

    return () => {
      if (mapInstance.current) {
        mapInstance.current.destroy();
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!mapLoaded || !window.ymaps) return;

    const initializeMap = () => {
      try {
        mapInstance.current = new window.ymaps.Map(mapRef.current, {
          center: [55.751244, 37.618423],
          zoom: 10,
          controls: []
        });

        mapInstance.current.events.add('click', (e) => {
          const coords = e.get('coords');
          handleMapClick(coords);
        });

      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initializeMap();
  }, [mapLoaded]);

  const handleMapClick = async (coords) => {
    setCoordinates({lat: coords[0], lng: coords[1]});

    if (placemarkRef.current) {
      mapInstance.current.geoObjects.remove(placemarkRef.current);
    }

    placemarkRef.current = createCustomPlacemark(coords);
    mapInstance.current.geoObjects.add(placemarkRef.current);

    // Получаем адрес
    window.ymaps.geocode(coords).then((res) => {
      const firstGeoObject = res.geoObjects.get(0);
      if (firstGeoObject) {
        setAddress(firstGeoObject.getAddressLine());
      }
    });

    await fetchWeather(coords[0], coords[1]);
  };

  const setMarkerFromInputCoordinates = async () => {
    if (!inputCoordinates?.latitude || !inputCoordinates?.longitude || !mapInstance.current) {
      return;
    }

    const latComplete = isCoordinateComplete(inputCoordinates.latitude);
    const lngComplete = isCoordinateComplete(inputCoordinates.longitude);

    if (!latComplete || !lngComplete) {
      return;
    }

    const lat = parseCoordinate(inputCoordinates.latitudeDirection, inputCoordinates.latitude);
    const lng = parseCoordinate(inputCoordinates.longitudeDirection, inputCoordinates.longitude);

    if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
      setCoordinates({lat, lng});

      mapInstance.current.setCenter([lat, lng], 15);

      if (placemarkRef.current) {
        mapInstance.current.geoObjects.remove(placemarkRef.current);
      }

      placemarkRef.current = createCustomPlacemark(
        [lat, lng],
        `Координаты из формы: ${lat.toFixed(6)}, ${lng.toFixed(6)}`
      );
      mapInstance.current.geoObjects.add(placemarkRef.current);

      window.ymaps.geocode([lat, lng]).then((res) => {
        const firstGeoObject = res.geoObjects.get(0);
        if (firstGeoObject) {
          setAddress(firstGeoObject.getAddressLine());
        }
      }).catch(error => {
        console.error('Ошибка геокодирования:', error);
      });

      await fetchWeather(lat, lng);
    }
  };

  useEffect(() => {
    if (mapLoaded && inputCoordinates?.latitude && inputCoordinates?.longitude) {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      updateTimeoutRef.current = setTimeout(() => {
        setMarkerFromInputCoordinates();
      }, 500);
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [inputCoordinates, mapLoaded]);

  const handleSearch = async (searchAddress) => {
    if (!mapInstance.current || !searchAddress.trim()) return;

    window.ymaps.geocode(searchAddress).then(async (res) => {
      const firstGeoObject = res.geoObjects.get(0);
      if (firstGeoObject) {
        const coords = firstGeoObject.geometry.getCoordinates();

        await handleMapClick(coords);
        mapInstance.current.setCenter(coords, 15);
      } else {
        toaster.push(
          <Message type="warning" closable>
            Адрес не найден
          </Message>,
          {placement: 'topCenter', duration: 2000}
        );
      }
    }).catch(error => {
      console.error('Search error:', error);
      toaster.push(
        <Message type="error" closable>
          Ошибка при поиске адреса
        </Message>,
        {placement: 'topCenter', duration: 2000}
      );
    });
  };

  const copyCoordinates = () => {
    if (coordinates) {
      const text = `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`;
      navigator.clipboard.writeText(text)
        .then(() => {
          toaster.push(
            <Message type="success" closable>
              Координаты скопированы!
            </Message>,
            {placement: 'topCenter', duration: 2000}
          );
        })
        .catch(() => {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);

          toaster.push(
            <Message type="info" closable>
              Координаты скопированы!
            </Message>,
            {placement: 'topCenter', duration: 2000}
          );
        });
    }
  };

  const useCoordinates = () => {
    if (coordinates && onUseCoordinates) {
      const formattedCoordinates = {
        latitude: coordinates.lat.toFixed(6),
        longitude: coordinates.lng.toFixed(6),
        address: address,
        weather: weather
      };

      onUseCoordinates(formattedCoordinates);

      toaster.push(
        <Message type="success" closable>
          Координаты переданы в форму!
        </Message>,
        {placement: 'topCenter', duration: 2000}
      );
    } else {
      toaster.push(
        <Message type="warning" closable>
          Сначала выберите точку на карте!
        </Message>,
        {placement: 'topCenter', duration: 2000}
      );
    }
  };

  const resetSelection = () => {
    setCoordinates(null);
    setAddress('');
    setWeather(null);
    lastWeatherCoordinatesRef.current = null;
    if (placemarkRef.current && mapInstance.current) {
      mapInstance.current.geoObjects.remove(placemarkRef.current);
      placemarkRef.current = null;
    }
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const refreshWeather = async () => {
    if (coordinates) {
      const weatherData = await fetchWeather(coordinates.lat, coordinates.lng, { force: true });

      if (weatherData) {
        toaster.push(
          <Message type="success" closable>
            Данные о погоде обновлены!
          </Message>,
          {placement: 'topCenter', duration: 2000}
        );
      }
    }
  };

  return (
    <div style={{border: '1px solid #ccc', padding: '20px', margin: '10px 0', borderRadius: '8px'}}>
      <div style={{marginBottom: '15px'}}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Введите адрес для поиска..."
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '10px'
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch(e.target.value);
            }
          }}
        />
        <button
          onClick={() => handleSearch(searchInputRef.current?.value || '')}
          style={{
            padding: '10px 15px',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Найти на карте
        </button>
        <button
          onClick={resetSelection}
          style={{
            padding: '10px 15px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Сбросить
        </button>
      </div>

      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '400px',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}
      >
        {!mapLoaded && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            background: '#f9f9f9'
          }}>
            <p>Загрузка карты...</p>
          </div>
        )}
      </div>

      {coordinates && (
        <div style={{
          marginTop: '15px',
          padding: '15px',
          background: '#f9f9f9',
          borderRadius: '4px',
          border: '1px solid #eee'
        }}>
          <h4>Выбранная точка:</h4>
          <p><strong>Широта:</strong> {coordinates.lat.toFixed(6)}</p>
          <p><strong>Долгота:</strong> {coordinates.lng.toFixed(6)}</p>
          {address && <p><strong>Адрес:</strong> {address}</p>}

          {weather && (
            <div style={{
              margin: '15px 0',
              padding: '15px',
              background: '#e3f2fd',
              borderRadius: '6px',
              border: '1px solid #bbdefb'
            }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <h5 style={{margin: 0}}>Погода:</h5>
                <button
                  onClick={refreshWeather}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Обновить
                </button>
              </div>

              <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                {weather.icon && (
                  <img
                    src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
                    alt={weather.description}
                    style={{width: '60px', height: '60px'}}
                  />
                )}
                <div style={{flex: 1}}>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                    <div>
                      <p style={{margin: '5px 0'}}><strong>Температура:</strong> {weather.temperature}°C</p>
                      <p style={{margin: '5px 0'}}><strong>Ощущается как:</strong> {weather.feelsLike}°C</p>
                    </div>
                    <div>
                      <p style={{margin: '5px 0'}}><strong>Давление:</strong> {weather.pressure} мм рт.ст.</p>
                      <p style={{margin: '5px 0'}}><strong>Влажность:</strong> {weather.humidity}%</p>
                    </div>
                  </div>
                  <p style={{margin: '5px 0'}}><strong>Описание:</strong> {weather.description}</p>
                  {weather.windSpeed && (
                    <p style={{margin: '5px 0'}}><strong>Ветер:</strong> {weather.windSpeed} м/с</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {loadingWeather && (
            <div style={{
              margin: '15px 0',
              padding: '15px',
              background: '#fff3e0',
              borderRadius: '6px',
              border: '1px solid #ffcc80',
              textAlign: 'center'
            }}>
              <p>Загрузка данных о погоде...</p>
            </div>
          )}

          {!weather && !loadingWeather && coordinates && (
            <div style={{
              margin: '15px 0',
              padding: '15px',
              background: '#ffebee',
              borderRadius: '6px',
              border: '1px solid #ffcdd2',
              textAlign: 'center'
            }}>
              <p>Не удалось загрузить данные о погоде</p>
              <button
                onClick={refreshWeather}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Попробовать снова
              </button>
            </div>
          )}

          <div style={{marginTop: '10px'}}>
            <button
              onClick={useCoordinates}
              style={{
                padding: '8px 15px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Использовать координаты
            </button>
            <button
              onClick={copyCoordinates}
              style={{
                padding: '8px 15px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Скопировать координаты
            </button>
            <button
              onClick={() => {
                const link = `https://yandex.ru/maps/?pt=${coordinates.lng},${coordinates.lat}&z=15`;
                window.open(link, '_blank');
              }}
              style={{
                padding: '8px 15px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Открыть в Яндекс.Картах
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default YandexMapComponent;
